// shared/src/auth/src/context/AuthProvider.jsx
import { onAuthStateChanged, getIdTokenResult } from 'firebase/auth';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { getFirebaseAuth as getAuthSingleton } from '../../../firebase/config';

const Ctx = createContext(null);
export const AuthContext = Ctx;
export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [claims, setClaims] = useState(null);
  const [claimsLoading, setClaimsLoading] = useState(true);
  
  useEffect(() => {
    const a = getAuthSingleton();
    const unsub = onAuthStateChanged(a, async (user) => {
      setUser(user);
      setLoading(false);
      
      if (user) {
        setClaimsLoading(true);
        try {
          // Small retry to reduce emulator race on custom claims
          let tokenResult = null;
          for (let i = 0; i < 3; i++) {
            tokenResult = await getIdTokenResult(user, true);
            if (tokenResult?.claims) break;
            await new Promise(r => setTimeout(r, 150));
          }
          
          const rawClaims = tokenResult?.claims ?? {};
          
          // Normalize claims extraction - check both role and systemRole
          const normalizedClaims = {
            role: rawClaims.role ?? rawClaims.systemRole ?? null,
            systemRole: rawClaims.systemRole ?? rawClaims.role ?? null,
            claims: rawClaims,
          };
          
          setClaims(normalizedClaims);
          
          // Extract role from normalized claims
          const userRole = normalizedClaims.role || 'none';
          setRole(userRole);
          
          if (import.meta.env?.DEV) {
            console.info('[AuthProvider] Claims normalized:', normalizedClaims);
          }
        } catch (error) {
          console.error('[AuthProvider] Failed to get claims:', error);
          setRole('none');
          setClaims(null);
        } finally {
          setClaimsLoading(false);
        }
      } else {
        setRole(null);
        setClaims(null);
        setClaimsLoading(false);
      }
    });
    return unsub;
  }, []);
  
  const value = useMemo(() => ({ 
    user, 
    loading,
    role,
    claims,
    claimsLoading,
    // Helper to refresh claims manually
    refreshClaims: async () => {
      if (!user) return;
      setClaimsLoading(true);
      try {
        const tokenResult = await getIdTokenResult(user, true);
        const rawClaims = tokenResult.claims ?? {};
        
        // Normalize claims extraction - check both role and systemRole
        const normalizedClaims = {
          role: rawClaims.role ?? rawClaims.systemRole ?? null,
          systemRole: rawClaims.systemRole ?? rawClaims.role ?? null,
          claims: rawClaims,
        };
        
        setClaims(normalizedClaims);
        const userRole = normalizedClaims.role || 'none';
        setRole(userRole);
        return { role: userRole, claims: normalizedClaims };
      } catch (error) {
        console.error('[AuthProvider] Failed to refresh claims:', error);
        return null;
      } finally {
        setClaimsLoading(false);
      }
    }
  }), [user, loading, role, claims, claimsLoading]);
  
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
export const useAuth = () => useContext(Ctx);

